import { Box, Stack, Tooltip, Typography, Grid } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import TypographyWithMiss from "components/ui/atoms/TypographyWithMiss";

const LabelValue = ({
  label,
  variant,
  isMissing,
  children,
  labelMinWidth,
  ...props
}) => {
  const [isLabelOverflowed, setIsLabelOverflow] = useState(false);
  const labelElementRef = useRef();
  useEffect(() => {
    setIsLabelOverflow(
      labelElementRef.current.scrollWidth > labelElementRef.current.clientWidth,
    );
  }, [label]);

  let labelVariant = "body2";
  if (variant == "ForListItem") labelVariant = "subtitle2";

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      justifyContent={"space-between"}
      {...props}
    >
      <Tooltip title={label} disableHoverListener={!isLabelOverflowed}>
        <Typography
          ref={labelElementRef}
          variant={labelVariant}
          flexShrink={1}
          overflow={"hidden"}
          minWidth={labelMinWidth || 30}
          noWrap
        >
          {label}
        </Typography>
      </Tooltip>
      {children}
    </Stack>
  );
};

export const LabelValueForText = ({ value, variant, isMissing, ...props }) => {
  let valueVariant = "body2";
  if (variant == "ForListItem") valueVariant = "caption";

  return (
    <LabelValue variant={variant} {...props}>
      <TypographyWithMiss
        variant={valueVariant}
        flexShrink={0}
        textAlign={"right"}
        sx={{ overflowWrap: "break-word", maxWidth: "calc(100% - 30px)" }}
        isMissing={isMissing}
      >
        {value}
      </TypographyWithMiss>
    </LabelValue>
  );
};

export const TextTagForLabelValue = ({ text, variant }) => {
  let valueVariant = "body2";
  if (variant == "ForListItem") valueVariant = "caption";

  return (
    <Typography
      variant="caption"
      noWrap
      overflow={"hidden"}
      textOverflow={"ellipsis"}
      display={"block"}
    >
      {text}
    </Typography>
  );
};

export const LabelValueForTags = ({
  Tags,
  maxTags,
  variant,
  isMissing,
  labelMinWidth = 30,
  ...props
}) => {
  let valueVariant = "body2";
  if (variant == "ForListItem") valueVariant = "caption";

  if (isMissing)
    return (
      <LabelValue variant={variant} labelMinWidth={labelMinWidth} {...props}>
        <TypographyWithMiss
          variant={valueVariant}
          flexShrink={0}
          textAlign={"right"}
          sx={{
            overflowWrap: "break-word",
            maxWidth: `calc(100% - ${labelMinWidth}px)`,
          }}
          isMissing={isMissing}
        ></TypographyWithMiss>
      </LabelValue>
    );

  return (
    <LabelValue variant={variant} {...props}>
      <Box maxWidth={`calc(100% - ${labelMinWidth}px)`}>
        <Grid
          container
          padding={0}
          spacing={1}
          justifyContent={"end"}
          wrap={maxTags ? "nowrap" : "wrap"}
          alignItems={"center"}
        >
          {Tags.slice(0, maxTags).map((Tag, index) => (
            <Grid key={`grid-tag-${index}`} maxWidth={"100%"}>
              {Tag}
            </Grid>
          ))}
          {Tags.length > maxTags && (
            <Grid>
              <Tooltip
                title={
                  <Grid spacing={1}>
                    {Tags.map((Tag, index) => (
                      <Grid key={`grid-tag-tooltip-${index}`}>{Tag}</Grid>
                    ))}
                  </Grid>
                }
              >
                <Typography variant={valueVariant} display={"block"}>
                  <b>+{Tags.length - maxTags}</b>
                </Typography>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Box>
    </LabelValue>
  );
};

export const LabelValueForComponent = ({
  maxTags,
  variant,
  isMissing,
  labelMinWidth = 30,
  children,
  ...props
}) => {
  let valueVariant = "body2";
  if (variant == "ForListItem") valueVariant = "caption";

  if (isMissing)
    return (
      <LabelValue variant={variant} labelMinWidth={labelMinWidth} {...props}>
        <TypographyWithMiss
          variant={valueVariant}
          flexShrink={0}
          textAlign={"right"}
          sx={{
            overflowWrap: "break-word",
            maxWidth: `calc(100% - ${labelMinWidth}px)`,
          }}
          isMissing={isMissing}
        ></TypographyWithMiss>
      </LabelValue>
    );

  return (
    <LabelValue variant={variant} {...props}>
      <Box
        width={`calc(100% - ${labelMinWidth}px)`}
        display={"flex"}
        flexGrow={1}
        alignItems={"center"}
        justifyContent={"end"}
      >
        {children}
      </Box>
    </LabelValue>
  );
};
